"use client";

import { motion } from "framer-motion";

export interface AboutProject2BlockStyle {
    backgroundColor?: string;
    textColor?: string;
    titleColor?: string;
    labelColor?: string;
    dividerColor?: string;
    paddingTop?: string;
    paddingBottom?: string;
}

export interface AboutProject2BlockProps {
    title: string;
    description: string;
    metadata: { label: string; value: string }[];
    style?: AboutProject2BlockStyle;
}

export default function AboutProject2Block({
    title,
    description,
    metadata = [],
    style
}: AboutProject2BlockProps) {
    return (
        <div
            className="w-full"
            style={{
                backgroundColor: style?.backgroundColor || "#111111",
                paddingTop: style?.paddingTop || "6rem",
                paddingBottom: style?.paddingBottom || "6rem",
                color: style?.textColor || "#FFFFFF"
            }}
        >
            <div className="container mx-auto px-4 md:px-12 lg:px-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">

                    {/* Left Column: Title & Metadata */}
                    <div className="lg:col-span-4">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-3xl md:text-4xl font-medium tracking-tight mb-8"
                            style={{ color: style?.titleColor || "#FFFFFF" }}
                        >
                            {title}
                        </motion.h2>

                        <motion.div
                            initial={{ scaleX: 0 }}
                            whileInView={{ scaleX: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="w-full h-px mb-8 origin-left"
                            style={{ backgroundColor: style?.dividerColor || "rgba(255,255,255,0.2)" }}
                        />

                        <div className="space-y-8">
                            {metadata.map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.3 + (idx * 0.1) }}
                                >
                                    <h4
                                        className="text-xs uppercase tracking-wider font-semibold mb-2"
                                        style={{ color: style?.labelColor || "#9CA3AF" }}
                                    >
                                        {item.label}
                                    </h4>
                                    <p className="text-base font-normal">
                                        {item.value}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Right Column: Description */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="lg:col-span-7 lg:col-start-6"
                    >
                        <div
                            className="text-lg md:text-xl leading-relaxed font-light space-y-6"
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </motion.div>

                </div>
            </div>
        </div>
    );
}
